/**
 * preloadCurrentRoute — load the code of the FIRST screen before the app draws.
 *
 * Why: every page is its own lazy chunk, and React Router only asks for it
 * once the main bundle has downloaded, run and matched the route. Until it
 * arrives React shows a fallback — on a cold load that was a dark page with
 * "Loading…" before the real UI (user report, 2026-10-23). main.jsx now waits
 * for the promise this returns and only then renders, so the first paint IS
 * the page. The wait is short: the likely first pages are already downloading
 * from the HTML itself (vite.config.js preloadFirstVisitPages), in parallel
 * with the main bundle.
 *
 * "First screen" follows the route table (routes.jsx) and the guards:
 *  - signed out: /login is the local form; any other address becomes the
 *    landing URL with the default sign-in screen (config/loginMode.js);
 *  - signed in: '/' is Home, /login moves on to the default section, a section
 *    is its page, and anything else is Not Found.
 */
import { getLoginFlags } from '@config/loginMode';
import { getDefaultPath, getSections } from '@config/navigation';

import { getBasename } from './basename';
import { HomePage, LocalLoginPage, NotFoundPage } from './lazyPages';

/** '/a/b/' -> '/a/b'; '' -> '/'. */
function normalize(pathname) {
  return `/${String(pathname ?? '').replace(/^\/+|\/+$/g, '')}`;
}

/**
 * The lazy page the first screen shows for `pathname` (basename already
 * removed), given whether the visitor is signed in.
 */
export function pageForPath(pathname, mode, { signedIn = true } = {}) {
  const path = normalize(pathname);

  if (!signedIn) {
    if (path === '/login') return LocalLoginPage;
    return getLoginFlags().localLogin ? LocalLoginPage : HomePage;
  }

  if (path === '/') return HomePage;
  if (path === '/login') return pageForPath(getDefaultPath(mode), mode);
  // Longest matching section first, so '/support/release-notes' beats '/support'.
  const match = getSections(mode)
    .filter((s) => path === s.path || path.startsWith(`${s.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0];
  return match?.page ?? NotFoundPage;
}

/**
 * Start loading the first screen's page. The promise settles when it has
 * arrived — or failed: it NEVER rejects, so a failed download still lets the
 * app render (the router then shows its own error).
 */
export function preloadCurrentRoute(mode, { signedIn = true } = {}) {
  try {
    if (typeof window === 'undefined') return Promise.resolve();
    const base = getBasename(mode);
    let { pathname } = window.location;
    if (base && (pathname === base || pathname.startsWith(`${base}/`))) {
      pathname = pathname.slice(base.length);
    }
    const page = pageForPath(pathname, mode, { signedIn });
    return Promise.resolve(page?.preload?.()).then(
      () => undefined,
      () => undefined,
    );
  } catch {
    return Promise.resolve();
  }
}
